// The call affordances: the quote screen's "Prefer to do this over the
// phone?" band (open / closed / booked faces) and the navbar's cluster
// (online status + number while we're open, Schedule a call after
// hours) that rides every step of the flow. Open/closed follows the
// app-wide business-hours clock.

import { useState } from "react";
import {
  CalendarDaysIcon,
  CheckIcon,
  PhoneIcon,
} from "@heroicons/react/24/outline";

import { SUPPORT_PHONE_HREF, SUPPORT_PHONE_LABEL } from "constants/phones";

import FlowModal from "./FlowModal";

import useIsBusinessHours from "hooks/useIsBusinessHours";

import type { ScheduledCall } from "./api";
import {
  bookedDayLabel,
  browserTimeZone,
  COMPANY_TIME_ZONE,
  slotTimeLabel,
  timeZoneShortLabel,
} from "./call-time";

// Outline only, deliberately: wherever the band renders, the flow's
// Continue is the primary CTA, and a filled call button would compete
// with it.
const BAND_BUTTON =
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-lg border-2 px-4 py-2.5 text-base font-bold transition-all";
const OUTLINE_BUTTON = `${BAND_BUTTON} border-soil bg-transparent text-soil hover:bg-soil hover:text-ivory`;

/** The company clock, dressed: "3:41 PM MT". */
function companyClockLabel(): string {
  const clock = slotTimeLabel(new Date().toISOString(), COMPANY_TIME_ZONE);
  const zone = timeZoneShortLabel(COMPANY_TIME_ZONE);
  return `${clock}${zone ? ` ${zone}` : ""}`;
}

function OnlineDot() {
  return (
    <span
      className="h-[9px] w-[9px] flex-none animate-pulse rounded-full bg-evergreen"
      aria-hidden="true"
    />
  );
}

function BookedBand({
  call,
  contactPhone,
  isOpen,
}: {
  call: ScheduledCall;
  contactPhone: string | null;
  isOpen: boolean;
}) {
  const timeZone = browserTimeZone();
  const day = bookedDayLabel(call.start, timeZone);
  const time = slotTimeLabel(call.start, timeZone);
  const zone = timeZoneShortLabel(timeZone);
  return (
    <div className="flex flex-col gap-3 rounded-2xl border-2 border-evergreen bg-oat p-4 2xs:p-5 sm:px-6 sm:py-5">
      <div className="flex items-center gap-3.5">
        <div className="flex h-[38px] w-[38px] flex-none items-center justify-center rounded-full bg-evergreen">
          <CheckIcon
            className="h-[18px] w-[18px] text-daisy"
            strokeWidth={3}
            aria-hidden="true"
          />
        </div>
        <h2 className="min-w-0 flex-1 font-serif text-[25px] leading-tight text-soil">
          Your call is set &mdash; {day} at {time}
          {zone ? ` ${zone}` : ""}
        </h2>
      </div>
      <p className="max-w-[48ch] text-base leading-normal text-oat-700">
        We&rsquo;ll call{" "}
        <span className="ph-no-capture">{contactPhone ?? "you"}</span>. Nothing
        else to do &mdash; and you&rsquo;re welcome to keep going here in the
        meantime.
      </p>
      {isOpen ? (
        <div className="flex justify-end">
          <a href={SUPPORT_PHONE_HREF} className={OUTLINE_BUTTON}>
            <OnlineDot />
            Or call us now
          </a>
        </div>
      ) : null}
    </div>
  );
}

const NAV_BUTTON =
  "flex h-[38px] items-center gap-1.5 whitespace-nowrap rounded-lg border-2 border-soil bg-transparent px-3.5 text-sm font-bold text-soil transition-all hover:bg-soil hover:text-ivory";

// Schedule wears no border in the bar — it is the quieter affordance
// beside the live number, and it still fills on hover. The transparent
// border keeps its box identical to NAV_BUTTON's, so the pair never
// misaligns.
const NAV_SCHEDULE_BUTTON =
  "flex h-[38px] items-center gap-1.5 whitespace-nowrap rounded-lg border-2 border-transparent bg-transparent px-3.5 text-sm font-bold text-soil transition-all hover:border-soil hover:bg-soil hover:text-ivory";

/**
 * The navbar's call cluster, present on every step of the flow. Desktop:
 * the online status + our number while we're open, Schedule a call after
 * hours. Mobile shows the same call affordance, sized down.
 */
export function NavCallActions({ onSchedule }: { onSchedule: () => void }) {
  const { isBusinessHours } = useIsBusinessHours();
  // The in-hours pill opens the call modal — call now, or schedule
  // instead. Out of hours every affordance goes straight to scheduling.
  const [callModalOpen, setCallModalOpen] = useState(false);

  const scheduleButton = (
    <button type="button" onClick={onSchedule} className={NAV_SCHEDULE_BUTTON}>
      <CalendarDaysIcon
        className="h-4 w-4"
        strokeWidth={2}
        aria-hidden="true"
      />
      Schedule a call
    </button>
  );

  const callUsNowButton = (
    <button
      type="button"
      className={NAV_BUTTON}
      onClick={() => setCallModalOpen(true)}
    >
      <PhoneIcon className="h-4 w-4" strokeWidth={2} aria-hidden="true" />
      Call us now
    </button>
  );

  return (
    <div className="flex items-center">
      <div className="hidden items-center gap-4 md:flex">
        {isBusinessHours ? (
          <>
            <span className="flex items-center gap-2">
              <OnlineDot />
              <span className="text-[13px] font-bold text-evergreen">
                We&rsquo;re online · {companyClockLabel()}
              </span>
            </span>
            {callUsNowButton}
          </>
        ) : (
          scheduleButton
        )}
      </div>
      <div className="md:hidden">
        {isBusinessHours ? (
          callUsNowButton
        ) : (
          <button type="button" onClick={onSchedule} className={NAV_BUTTON}>
            Schedule a call
          </button>
        )}
      </div>
      {callModalOpen && (
        <CallModal
          onClose={() => setCallModalOpen(false)}
          onSchedule={() => {
            setCallModalOpen(false);
            onSchedule();
          }}
        />
      )}
    </div>
  );
}

/** The call modal: the number to tap now, or scheduling instead — the
 * house modal shell, so mobile gets the full-screen card with an X. */
function CallModal({
  onClose,
  onSchedule,
}: {
  onClose: () => void;
  onSchedule: () => void;
}) {
  const optionCard =
    "flex w-full items-center gap-3.5 rounded-xl border border-oat-200 px-5 py-4 text-left transition-colors";
  return (
    <FlowModal icon={PhoneIcon} title="Talk to someone" onClose={onClose}>
      <div className="flex flex-col gap-3">
        <a
          href={SUPPORT_PHONE_HREF}
          className={`${optionCard} bg-white hover:border-evergreen`}
          onClick={onClose}
        >
          <PhoneIcon
            className="h-6 w-6 flex-none text-soil"
            strokeWidth={2}
            aria-hidden="true"
          />
          <span className="flex flex-col">
            <span className="ph-no-capture text-xl font-bold leading-tight text-soil">
              {SUPPORT_PHONE_LABEL}
            </span>
            <span className="mt-1 flex items-center gap-1.5 text-sm text-oat-700">
              <OnlineDot />
              We&rsquo;re online now · {companyClockLabel()}
            </span>
          </span>
        </a>
        <button
          type="button"
          className={`${optionCard} bg-transparent hover:border-evergreen`}
          onClick={onSchedule}
        >
          <CalendarDaysIcon
            className="h-6 w-6 flex-none text-evergreen"
            strokeWidth={2}
            aria-hidden="true"
          />
          <span className="text-base font-bold text-evergreen">
            Schedule a call instead
          </span>
        </button>
      </div>
    </FlowModal>
  );
}
export default function PhoneBand({
  bookedCall,
  contactPhone,
  onSchedule,
}: {
  bookedCall: ScheduledCall | null;
  contactPhone: string | null;
  onSchedule: () => void;
}) {
  const { isBusinessHours } = useIsBusinessHours();

  if (bookedCall) {
    return (
      <BookedBand
        call={bookedCall}
        contactPhone={contactPhone}
        isOpen={isBusinessHours}
      />
    );
  }

  return (
    <div className="flex flex-col gap-4 rounded-2xl bg-oat p-4 2xs:p-5 sm:px-6 sm:py-6 lg:flex-row lg:items-center lg:gap-7">
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-3.5">
          <div className="flex h-[38px] w-[38px] flex-none items-center justify-center rounded-full bg-ivory-light">
            <PhoneIcon
              className="h-[18px] w-[18px] text-evergreen"
              strokeWidth={2}
              aria-hidden="true"
            />
          </div>
          <h2 className="min-w-0 flex-1 font-serif text-[25px] leading-tight text-soil">
            Prefer to do this over the phone?
          </h2>
        </div>
        <p className="mt-2 text-base leading-normal text-oat-700">
          {isBusinessHours
            ? "A licensed pre-planning agent can compare the plans with you, answer anything, or finish this over the phone. We pick up — no queue, no callback."
            : "Book 15 minutes with a licensed pre-planning agent. They’ll compare the plans with you, answer anything, and hold this quote at today’s price."}
        </p>
      </div>
      <div className="flex flex-none flex-col gap-2.5">
        <div className="flex flex-col gap-2.5 sm:flex-row">
          {isBusinessHours ? (
            <a href={SUPPORT_PHONE_HREF} className={OUTLINE_BUTTON}>
              <PhoneIcon
                className="h-4 w-4"
                strokeWidth={2}
                aria-hidden="true"
              />
              Call <span className="ph-no-capture">{SUPPORT_PHONE_LABEL}</span>
            </a>
          ) : null}
          <button type="button" onClick={onSchedule} className={OUTLINE_BUTTON}>
            <CalendarDaysIcon
              className="h-4 w-4"
              strokeWidth={2}
              aria-hidden="true"
            />
            Schedule a call
          </button>
        </div>
        {isBusinessHours ? (
          <div className="flex items-center gap-2 lg:justify-center">
            <OnlineDot />
            <span className="text-[13px] font-bold text-evergreen">
              We&rsquo;re online now · {companyClockLabel()}
            </span>
          </div>
        ) : null}
      </div>
    </div>
  );
}
